/**
 * Centralized API Configuration
 *
 * This module provides a single source of truth for all API endpoints.
 * It automatically adapts to different deployment environments:
 * - Development: Uses VITE_API_URL from .env.development
 * - Production: Uses relative paths or VITE_API_URL from .env.production
 * - Docker: Works with proxy configuration
 * - AWS/Cloud: Works with any IP/domain
 *
 * NEVER hardcode URLs anywhere else in the application!
 */

/**
 * Get the base API URL based on environment
 */
function getApiBaseUrl(): string {
  // 1. Try environment variable first (from .env files)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // 2. In production, use relative path (assumes same origin or proxy)
  if (import.meta.env.PROD) {
    return "/api/v1";
  }

  // 3. Development fallback - throw error instead of using hardcoded value
  throw new Error(
    "VITE_API_URL environment variable is not set. " +
      "Please create a .env.development file with VITE_API_URL=http://your-backend-url:port/api/v1"
  );
}

/**
 * Get the WebSocket URL based on environment.
 *
 * Em produção (build de prod / aws-test) usamos SEMPRE `window.location.host`,
 * mesmo que `VITE_WS_URL` esteja setado. Razão: o frontend e o backend rodam
 * atrás do MESMO reverse proxy (Traefik) sob o mesmo host:porta. Se o IP da
 * VM mudar (ex: VM sem Static IP rebootou), o build-time `VITE_WS_URL` aponta
 * pro IP fantasma e WebSocket falha pra sempre. Em runtime via `location.host`,
 * o WS sempre acompanha o host onde o navegador carregou a página.
 *
 * Em dev (`!PROD`), aceitar `VITE_WS_URL` ou derivar de `VITE_API_URL`,
 * porque dev local roda backend em porta separada (8000) do Vite (8080) e
 * `window.location.host` aponta pro Vite, não pro backend.
 */
function getWebSocketUrl(): string {
  // 1. Em produção/aws-test: runtime, NÃO build-time (resiliente a mudança de IP)
  if (import.meta.env.PROD) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}`;
  }

  // 2. Dev: tenta env var explícita
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  // 3. Dev: deriva de VITE_API_URL
  if (import.meta.env.VITE_API_URL) {
    const apiUrl = import.meta.env.VITE_API_URL;
    return apiUrl.replace(/^http/, "ws").replace(/\/api\/v1$/, "");
  }

  // 4. Dev sem config — erro explícito
  throw new Error(
    "VITE_WS_URL or VITE_API_URL environment variable is not set. " +
      "Please create a .env.development file with VITE_WS_URL=ws://your-backend-url:port"
  );
}

/**
 * Main API configuration object
 */
export const apiConfig = {
  /**
   * Base URL for all HTTP API requests
   * Example: "http://10.113.1.240:8000/api/v1" or "/api/v1"
   */
  baseURL: getApiBaseUrl(),

  /**
   * Base URL for WebSocket connections
   * Example: "ws://10.113.1.240:8000" or "wss://api.example.com"
   */
  wsBaseURL: getWebSocketUrl(),

  /**
   * Request timeout in milliseconds
   */
  timeout: 30000,

  /**
   * Default headers for all requests
   */
  headers: {
    "Content-Type": "application/json",
  },
} as const;

/**
 * Helper to build full API endpoint URL
 * @param path - API path (e.g., "/users", "/equipments")
 * @returns Full URL
 */
export function getApiUrl(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;

  // If baseURL already includes /api/v1, don't duplicate it
  if (apiConfig.baseURL.endsWith("/api/v1")) {
    return `${apiConfig.baseURL}/${cleanPath}`;
  }

  return `${apiConfig.baseURL}/api/v1/${cleanPath}`;
}

/**
 * Helper to build WebSocket endpoint URL
 * @param path - WebSocket path (e.g., "/ws/equipment", "/ws/terminals")
 * @returns Full WebSocket URL
 */
export function getWsUrl(path: string): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${apiConfig.wsBaseURL}${cleanPath}`;
}

/**
 * Environment information (for debugging)
 */
export const envInfo = {
  mode: import.meta.env.MODE,
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  apiUrl: apiConfig.baseURL,
  wsUrl: apiConfig.wsBaseURL,
} as const;

// Log configuration on load (only in development)
if (import.meta.env.DEV) {
  console.log("🔧 API Configuration:", envInfo);
}

export default apiConfig;
